// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI XR Studio — what a render draws over the globe (docs/AYNI_XR_STUDIO_TECH_DESIGN.md
 * §5, plan items J4 and J5), on a 2D canvas so every frame is the same on any machine:
 *
 * - **the data credit**, always, inside title-safe at the bottom (audit §13 Q10:
 *   burned in, not removable). A frame without a credit line is refused, since
 *   every source asks for at least a credit;
 * - **the date** of the data on screen, top left;
 * - **the legend** (the dataset's colour bar), bottom right;
 * - **the brand kit**: logo top right and an optional lower third;
 * - **captions**: a dot and a label at a place, with the value read from the
 *   data at that frame, the same reading as the pointer.
 *
 * Sizes follow the output height, so 1080p and 8K look alike.
 *
 * Version 2 shots (stops): the credit and legend follow the dataset of the
 * stop on screen (`current`), each stop's effects are painted during its
 * hold (`effects.ts`), the brand can slide in and out, and an end card
 * lists every credit the video used. The credit is drawn after the effects,
 * so a title card or a spotlight never hides it.
 */

import { captionsAt, frameTime, sampleShot, SAFE_AREA, type Shot } from './shot'
import type { FrameCompositor } from './renderMode'
import { paintEffects } from './effects'

export interface BrandKit {
  logo: CanvasImageSource | null
  /** Lower-third text, e.g. the outlet's programme or story line. */
  lowerThird: string | null
  /** Accent colour for the lower third band, CSS colour. */
  accent: string
  /** Font family for every drawn text. */
  font: string
}

export interface CompositorInputs {
  shot: Shot
  width: number
  height: number
  /** The credit lines of every layer on screen, in order; at least one. */
  credits: readonly string[]
  /** The dataset's colour bar, or null. */
  legend: CanvasImageSource | null
  brand: BrandKit | null
  /** The data date as the viewer should read it (the data's precision). */
  formatDate(ms: number): string
  /** A place on screen, or null when it is behind the globe or off the frame. */
  project(lat: number, lon: number): { x: number; y: number } | null
  /** The value at a place with its unit ("34.2 °C"), or null where there is no data. */
  readValue(lat: number, lon: number): string | null
  /** Version 2: whether a split compare is drawn on the globe now (its line and dates are painted only then). */
  compareShown?(): boolean
  /** Version 2: the credit lines and legend of the stop on screen now (they change with its dataset). */
  current?(): { credits: readonly string[]; legend: CanvasImageSource | null }
  /** Version 2: every credit the video has shown, for the end card. */
  allCredits?(): readonly string[]
  /** The language a title card is read in. */
  language?: string
  /** Words the end card needs, in the viewer's language. */
  labels?: { endCardTitle: string; madeWith: string }
  /** Makes an offscreen canvas (blur and grain need one). */
  makeCanvas?(w: number, h: number): HTMLCanvasElement
  /**
   * AYNI (multi-globe plan, a split render): each globe's box in the picture
   * (output pixels) and its own date; when there are several, each globe is
   * labelled in its corner instead of the one date top left.
   */
  globeLabels?(): ReadonlyArray<{ x: number; y: number; w: number; h: number; text: string }>
}

export class MissingCreditError extends Error {
  constructor() {
    super('a render needs the data credit of every layer on screen')
    this.name = 'MissingCreditError'
  }
}

const DEFAULT_FONT = '"Inter", "Segoe UI", system-ui, sans-serif'

/** Break `text` into lines no wider than `maxWidth` as measured by `ctx`. */
export function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const lines: string[] = []
  let line = ''
  for (const word of text.split(/\s+/).filter(Boolean)) {
    const next = line ? `${line} ${word}` : word
    if (line && ctx.measureText(next).width > maxWidth) {
      lines.push(line)
      line = word
    } else {
      line = next
    }
  }
  if (line) lines.push(line)
  return lines
}

const ANIMATE_IN = 0.8
const ANIMATE_OUT = 0.6

export function createCompositor(inputs: CompositorInputs): FrameCompositor {
  const credits = inputs.credits.map((c) => c.trim()).filter(Boolean)
  // A version 1 shot shows one dataset's credit throughout; version 2 reads it per stop, checked as each stop is put up.
  if (!credits.length && !inputs.current) throw new MissingCreditError()
  const { width: W, height: H, shot } = inputs
  const font = inputs.brand?.font || DEFAULT_FONT
  const unit = H / 1080 // 1 at 1080p
  const left = W * SAFE_AREA.title
  const right = W * (1 - SAFE_AREA.title)
  const top = H * SAFE_AREA.title
  const bottom = H * (1 - SAFE_AREA.title)

  function panel(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void {
    ctx.fillStyle = 'rgba(0, 0, 0, 0.55)'
    ctx.fillRect(x, y, w, h)
  }

  function drawCredit(ctx: CanvasRenderingContext2D, onScreen: readonly string[]): number {
    const size = Math.round(15 * unit)
    ctx.font = `${size}px ${font}`
    ctx.textBaseline = 'top'
    const lines = onScreen.flatMap((c) => wrapText(ctx, c, (right - left) * 0.62))
    if (!lines.length) throw new MissingCreditError()
    const lineH = size * 1.3
    const pad = 8 * unit
    const blockH = lines.length * lineH + pad * 2
    const y = bottom - blockH
    const blockW = Math.max(...lines.map((l) => ctx.measureText(l).width)) + pad * 2
    panel(ctx, left, y, blockW, blockH)
    ctx.fillStyle = '#ffffff'
    lines.forEach((l, i) => ctx.fillText(l, left + pad, y + pad + i * lineH))
    return y
  }

  function drawDate(ctx: CanvasRenderingContext2D, ms: number): void {
    const size = Math.round(30 * unit)
    ctx.font = `600 ${size}px ${font}`
    ctx.textBaseline = 'top'
    const text = inputs.formatDate(ms)
    const pad = 10 * unit
    panel(ctx, left, top, ctx.measureText(text).width + pad * 2, size + pad * 2)
    ctx.fillStyle = '#ffffff'
    ctx.fillText(text, left + pad, top + pad)
  }

  /** A split render: each globe's own date, in its top left corner (inside the title-safe area). */
  function drawGlobeLabels(ctx: CanvasRenderingContext2D, globes: ReadonlyArray<{ x: number; y: number; w: number; h: number; text: string }>): void {
    const size = Math.round(24 * unit)
    ctx.font = `600 ${size}px ${font}`
    ctx.textBaseline = 'top'
    const pad = 8 * unit
    for (const g of globes) {
      if (!g.text) continue
      const x = Math.max(left, g.x + 12 * unit)
      const y = Math.max(top, g.y + 12 * unit)
      const text = wrapText(ctx, g.text, Math.max(40 * unit, g.w - (x - g.x) - pad * 3))[0] ?? g.text
      panel(ctx, x, y, ctx.measureText(text).width + pad * 2, size + pad * 2)
      ctx.fillStyle = '#ffffff'
      ctx.fillText(text, x + pad, y + pad)
    }
  }

  function drawLegend(ctx: CanvasRenderingContext2D, legend: CanvasImageSource | null): void {
    const img = legend as (CanvasImageSource & { width?: number; height?: number }) | null
    if (!img || !img.width || !img.height) return
    const h = 70 * unit
    const w = Math.min((img.width / img.height) * h, W * 0.3)
    ctx.drawImage(img, right - w, bottom - h, w, h)
  }

  /** The logo top right, and the lower third just above the credit block (which starts at `creditTop`); `shown` 0–1 slides them. */
  function drawBrand(ctx: CanvasRenderingContext2D, creditTop: number, shown = 1): void {
    const brand = inputs.brand
    if (!brand || shown <= 0) return
    const ease = 1 - Math.pow(1 - shown, 3)
    ctx.save()
    ctx.globalAlpha = Math.min(1, shown * 1.5)
    const logo = brand.logo as (CanvasImageSource & { width?: number; height?: number }) | null
    if (logo && logo.width && logo.height) {
      const h = 64 * unit
      const w = (logo.width / logo.height) * h
      ctx.drawImage(logo, right - w + (1 - ease) * (w + 40 * unit), top, w, h)
    }
    ctx.translate(-(1 - ease) * W * 0.4, 0)
    if (brand.lowerThird) {
      const size = Math.round(34 * unit)
      ctx.font = `600 ${size}px ${font}`
      ctx.textBaseline = 'middle'
      const bandH = size * 1.8
      const y = creditTop - bandH - 12 * unit
      const w = ctx.measureText(brand.lowerThird).width + 40 * unit
      ctx.fillStyle = brand.accent
      ctx.fillRect(left, y, w, bandH)
      ctx.fillStyle = '#ffffff'
      ctx.fillText(brand.lowerThird, left + 20 * unit, y + bandH / 2)
    }
    ctx.restore()
  }

  /** How far the brand has slid in (version 2 with an animated brand): in at the start, out before the end card. */
  function brandShown(t: number): number {
    if (!shot.film || !shot.finish.animatedBrand) return 1
    const end = shot.film.seconds
    return Math.max(0, Math.min(1, t / ANIMATE_IN, (end - t) / ANIMATE_OUT))
  }

  /** The closing card: every credit the video used and the AYNI mark, over the blurred last frame. */
  function drawEndCard(ctx: CanvasRenderingContext2D, progress: number, clean: HTMLCanvasElement | undefined): void {
    const a = Math.min(1, progress / 0.15)
    ctx.save()
    ctx.globalAlpha = a
    if (clean) { ctx.filter = `blur(${Math.round(20 * unit)}px)`; ctx.drawImage(clean, 0, 0, W, H); ctx.filter = 'none' }
    ctx.fillStyle = 'rgba(0, 0, 0, 0.78)'
    ctx.fillRect(0, 0, W, H)
    const all = (inputs.allCredits?.() ?? credits).map((c) => c.trim()).filter(Boolean)
    const titleSize = Math.round(40 * unit)
    const size = Math.round(20 * unit)
    const maxW = (right - left) * 0.8
    ctx.font = `${size}px ${font}`
    const lines = all.flatMap((c, i) => [...wrapText(ctx, c, maxW), ...(i < all.length - 1 ? [''] : [])])
    const lineH = size * 1.35
    const blockH = titleSize * 1.6 + lines.length * lineH + 60 * unit
    let y = Math.max(top, H / 2 - blockH / 2)
    const x = W / 2 - maxW / 2
    ctx.fillStyle = inputs.brand?.accent ?? '#c0392b'
    ctx.fillRect(x, y, 80 * unit, 6 * unit)
    y += 24 * unit
    ctx.fillStyle = '#ffffff'
    ctx.textBaseline = 'top'
    ctx.font = `700 ${titleSize}px ${font}`
    ctx.fillText(inputs.labels?.endCardTitle ?? 'Data', x, y)
    y += titleSize * 1.6
    ctx.font = `${size}px ${font}`
    for (const l of lines) { ctx.fillText(l, x, y); y += lineH }
    ctx.font = `600 ${Math.round(18 * unit)}px ${font}`
    ctx.fillStyle = 'rgba(255, 255, 255, 0.75)'
    ctx.fillText(inputs.labels?.madeWith ?? 'AYNI XR · Pachamama Studios', x, Math.min(bottom - 24 * unit, y + 30 * unit))
    ctx.restore()
  }

  function drawCaptions(ctx: CanvasRenderingContext2D, t: number): void {
    const size = Math.round(24 * unit)
    for (const caption of captionsAt(shot, t)) {
      const at = inputs.project(caption.lat, caption.lon)
      if (!at || at.x < 0 || at.y < 0 || at.x > W || at.y > H) continue
      const value = inputs.readValue(caption.lat, caption.lon)
      const text = value ? `${caption.label} · ${value}` : caption.label
      ctx.fillStyle = '#ffffff'
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.8)'
      ctx.lineWidth = 3 * unit
      ctx.beginPath()
      ctx.arc(at.x, at.y, 6 * unit, 0, Math.PI * 2)
      ctx.fill()
      ctx.stroke()
      ctx.font = `600 ${size}px ${font}`
      ctx.textBaseline = 'middle'
      const x = at.x + 14 * unit
      ctx.strokeText(text, x, at.y)
      ctx.fillText(text, x, at.y)
    }
  }

  return {
    draw(ctx, i, clean) {
      const t = frameTime(shot.output, i)
      const sample = sampleShot(shot, t)
      if (sample.endCard !== null && sample.endCard !== undefined) {
        drawEndCard(ctx, sample.endCard, clean)
        return
      }
      const now = inputs.current?.() ?? { credits, legend: inputs.legend }
      const film = sample.film
      if (shot.film && film?.phase === 'hold') {
        const stop = shot.film.stops[film.stop]
        const otherMs = stop.effects.compare?.at ? Date.parse(stop.effects.compare.at) : NaN
        const compareLabels = sample.atMs !== null && Number.isFinite(otherMs) && inputs.compareShown?.()
          ? { here: inputs.formatDate(sample.atMs), there: inputs.formatDate(otherMs) }
          : null
        paintEffects(ctx, stop.effects, { local: film.local, hold: stop.hold }, {
          width: W, height: H, project: inputs.project, readValue: inputs.readValue, source: clean ?? null,
          makeCanvas: inputs.makeCanvas, language: inputs.language ?? 'en', accent: inputs.brand?.accent ?? '#c0392b', font, frame: i,
          compareLabels,
        })
      }
      if (!shot.film) drawCaptions(ctx, t)
      const globes = inputs.globeLabels?.() ?? []
      if (globes.length > 1) drawGlobeLabels(ctx, globes)
      else if (sample.atMs !== null) drawDate(ctx, sample.atMs)
      const creditTop = drawCredit(ctx, now.credits)
      drawBrand(ctx, creditTop, brandShown(t))
      drawLegend(ctx, now.legend)
    },
  }
}
